import React from 'react';
import {
  Button,
  Field,
  Input,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { ClipboardTaskListLtr24Regular } from '@fluentui/react-icons';
import { SectionShell } from './SectionShell';

// A generic key/value "Details" block built on SectionShell. In read-only mode
// (`items`), rows with empty values are omitted and an explanatory hint shows
// when every value is empty. In edit mode (`editableFields`), each field renders
// as an inline input the parent owns; Save/Discard surface once the form differs
// from what's persisted (`dirty`).
export interface DetailItem {
  label: string;
  value?: string | null;
}

export interface EditableDetailField {
  key: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface DetailsSectionProps {
  items?: DetailItem[];
  /** When provided, the block renders editable inputs instead of read-only rows.
   *  The parent holds the field state and the save mutation. */
  editableFields?: EditableDetailField[];
  /** Edit-mode: persist the current field values. */
  onSave?: () => void;
  /** Edit-mode: revert the fields to their last-saved values. */
  onDiscard?: () => void;
  /** Edit-mode: the form differs from what's persisted (drives Save/Discard). */
  dirty?: boolean;
  /** Edit-mode: a save is in flight. */
  saving?: boolean;
  /** Edit-mode: message from a failed save. */
  saveError?: string | null;
  /** Shown in read-only mode when no item has a value. */
  emptyHint?: string;
  showIcon?: boolean;
}

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  value: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  empty: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
  },
  // Edit mode: stacked inputs + a trailing actions row.
  form: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    ...shorthands.gap('8px'),
    marginTop: '4px',
  },
});

const DetailsSectionBase: React.FC<DetailsSectionProps> = ({
  items,
  editableFields,
  onSave,
  onDiscard,
  dirty = false,
  saving = false,
  saveError,
  emptyHint,
  showIcon = true,
}) => {
  const styles = useStyles();

  if (editableFields) {
    return (
      <SectionShell title="Details" icon={<ClipboardTaskListLtr24Regular />} showIcon={showIcon}>
        <div className={styles.form}>
          {editableFields.map((f) => (
            <Field key={f.key} label={f.label}>
              <Input
                value={f.value}
                placeholder={f.placeholder}
                onChange={(_e, data) => f.onChange(data.value)}
              />
            </Field>
          ))}
          {saveError && (
            <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
              {saveError}
            </Text>
          )}
          {dirty && (
            <div className={styles.actions}>
              <Button appearance="subtle" onClick={onDiscard} disabled={saving}>
                Discard
              </Button>
              <Button appearance="primary" onClick={onSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save details'}
              </Button>
            </div>
          )}
        </div>
      </SectionShell>
    );
  }

  const filled = (items || []).filter((i) => i.value != null && String(i.value).trim() !== '');
  return (
    <SectionShell title="Details" icon={<ClipboardTaskListLtr24Regular />} showIcon={showIcon}>
      {filled.length > 0 ? (
        <div className={styles.list}>
          {filled.map((item) => (
            <div key={item.label} className={styles.row}>
              <span className={styles.label}>{item.label}</span>
              <span className={styles.value}>{item.value}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className={styles.empty}>
          {emptyHint || 'No details added yet.'}
        </div>
      )}
    </SectionShell>
  );
};

export const DetailsSection = React.memo(DetailsSectionBase);
export default DetailsSection;
